<template>
  <button
    v-if="sceneTree.activeScene"
    class="net-visibility-button button-icon"
    ref="visibilityButton"
    type="button"
    :class="{ '-active': visibilityDialog.visible }"
    @click="toggleVisibilityDialog"
  >
    <lucide-icon :name="'eye'" />

    <floating-element
      v-if="visibilityDialog.visible"
      :x="visibilityDialog.x"
      :x-offset="visibilityDialog.xOffset"
      :y="visibilityDialog.y"
      y-alignment="bottom"
      x-alignment="right"
      :non-closing-elements="visibilityButton ? [visibilityButton] : []"
      class="net-visibility-button__dialog"
      @click:outside="onClickOutsideDialog"
    >
      <div class="net-visibility-button__grid">
        <button
          type="button"
          id="net-visibility-question"
          class="button-icon p-1"
          @click="toggleQuestionVisibility()"
        >
          <lucide-icon :name="question.hidden ? 'eye-off' : 'eye'" />
        </button>
        <label
          for="net-visibility-question"
          class="net-visibility-button__label"
          >{{ $t("labels.question") }}</label
        >

        <hr>

        <net-visibility-button-select
          id="net-visibility-elementsDisplay"
          :value="sceneTree.activeScene.elementsDisplayMode"
          :options="['hidden', 'value', 'impacts']"
          :non-closing-elements="elementsLabel ? [elementsLabel] : []"
          class="p-1"
          v-slot="{ value: displayMode }"
          @update:value="sceneStore.updateKey(sceneTree.activeScene.id, 'elementsDisplayMode', $event)"
        >
          {{ $t(`elementDisplayModes.${displayMode}`) }}
        </net-visibility-button-select>
        <label
          ref="elementsLabel"
          for="net-visibility-elementsDisplay"
          class="net-visibility-button__label"
        >
          {{ $t("labels.elementStatusBar") }}
        </label>

        <hr v-if="relationStore.length + flowStore.length > 0">

        <template v-if="relationStore.length > 0">
          <net-visibility-button-select
            id="net-visibility-impactRelations"
            :value="sceneTree.activeScene.linkRelationsDisplay"
            :options="['visible', 'hidden', 'endpoints', 'compact']"
            :non-closing-elements="linkLabel ? [linkLabel] : []"
            class="p-1"
            v-slot="{ value: displayMode }"
            @update:value="sceneStore.updateKey(sceneTree.activeScene.id, 'linkRelationsDisplay', $event)"
          >
            {{ $t(`relationDisplayModes.${displayMode}`) }}
          </net-visibility-button-select>
          <label
            ref="linkLabel"
            for="net-visibility-impactRelations"
            class="net-visibility-button__label"
            >{{ $t("objectNames.linkRelation", MANY) }}</label
          >
        </template>

        <template v-if="flowStore.length > 0">
          <net-visibility-button-select
            id="net-visibility-flowRelations"
            :value="sceneTree.activeScene.flowRelationsDisplay"
            :options="['visible', 'hidden', 'endpoints']"
            :non-closing-elements="flowLabel ? [flowLabel] : []"
            class="p-1"
            v-slot="{ value: displayMode }"
            @update:value="sceneStore.updateKey(sceneTree.activeScene.id, 'flowRelationsDisplay', $event)"
          >
            {{ $t(`relationDisplayModes.${displayMode}`) }}
          </net-visibility-button-select>
          <label
            ref="flowLabel"
            for="net-visibility-flowRelations"
            class="net-visibility-button__label"
            >{{ $t("objectNames.flowRelation", MANY) }}</label
          >
        </template>

        <template v-if="flowStore.length > 0 || indexStore.length > 0">
          <button
            type="button"
            id="net-visibility-indexlines"
            class="button-icon p-1"
            @click="toggleIndexconnectionsVisibility()"
          >
            <lucide-icon :name="sceneTree.activeScene.indexConnectionsVisible ? 'eye' : 'eye-off'" />
          </button>
          <label
            for="net-visibility-indexlines"
            class="net-visibility-button__label"
            >{{ $t("labels.indexLines") }}</label
          >
        </template>
      </div>
    </floating-element>
  </button>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";

import NetVisibilityButtonSelect from "./NetVisibilityButtonSelect.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import FloatingElement from "@core/ui/FloatingElement.vue";

import { useFlowStore } from "@editor/items/flow/flows.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useQuestionStore } from "@editor/items/question/question.store";
import { useSceneTree } from "@editor/items/scene/sceneTree";
import { useSceneStore } from "@editor/items/scene/scenes.store";

import type { Handleable } from "@core/types/Events";

const MANY = 1000;

const visibilityButton = ref<HTMLElement>();
const elementsLabel = ref<HTMLElement>();
const linkLabel = ref<HTMLElement>();
const flowLabel = ref<HTMLElement>();

const sceneTree = useSceneTree();
const sceneStore = useSceneStore();
const question = useQuestionStore();
const flowStore = useFlowStore();
const indexStore = useIndexStore();
const relationStore = useRelationStore();

// ================================
// STATE CHANGES

function toggleQuestionVisibility() {
  question.hidden = !question.hidden;
}

function toggleIndexconnectionsVisibility() {
  sceneStore.updateKey(sceneTree.activeScene.id, "indexConnectionsVisible", !sceneTree.activeScene.indexConnectionsVisible);
}

// ================================
// DIALOG

const visibilityDialog = reactive({
  visible: false,
  x: 0,
  xOffset: 0,
  y: 0
});

function onClickOutsideDialog(event: MouseEvent & Handleable) {
  if (event.defaultPrevented) return;
  closeVisibilityDialog();
}

function toggleVisibilityDialog(mouseEvent: MouseEvent) {
  if (visibilityDialog.visible) {
    closeVisibilityDialog();
  } else {
    openVisibilityDialog(mouseEvent);
  }
}

function openVisibilityDialog(mouseEvent: MouseEvent) {
  visibilityDialog.visible = true;

  if (visibilityButton.value) {
    const bounding = visibilityButton.value.getBoundingClientRect();
    visibilityDialog.x = bounding.right;
    visibilityDialog.y = bounding.top;
  } else {
    visibilityDialog.x = mouseEvent.clientX;
    visibilityDialog.y = mouseEvent.clientY;
  }
}

function closeVisibilityDialog() {
  console.warn("close visibility dialog");
  visibilityDialog.visible = false;
}
</script>

<style lang="scss">
.net-visibility-button {
  position: relative;
  padding-left: 0.25rem;
  padding-right: 0.25rem;

  &.-active {
    color: var(--color-primary);
  }

  &__dialog {
    padding: 0.75rem;
    background-color: var(--color-background-deep);
    border-radius: 0.25rem;
    box-shadow: 0px 1px 4px var(--color-shadow-2);
  }

  &__grid {
    display: grid;
    grid-template-columns: 2rem 1fr;
    column-gap: 0.25rem;
    row-gap: 0.5rem;
    align-items: center;

    hr {
      grid-column-start: 1;
      grid-column-end: 3;
      margin: 0;
    }
  }

  &__label {
    text-align: left;
    user-select: none;
  }
}
</style>
